/**
 * Lenguaje humano por vertical de pagafácil.
 *
 * Evita términos técnicos como "share" o "miembro genérico" y usa la palabra
 * que el usuario espera en su contexto: papeleta, entrada, cuota, reserva...
 */

export type GroupBuyType =
  | 'raffle_share'
  | 'papeleta_sitio'
  | 'ticket_share'
  | 'membership_quota'
  | 'group_gift'
  | 'event_crowdfund'
  | 'porra';

interface VerticalCopy {
  emoji: string;
  /** Etiqueta corta del tipo (sustituye "tipo") */
  shortLabel: string;
  /** Singular: "papeleta", "entrada", "cuota"... */
  unitSingular: string;
  /** Plural: "papeletas", "entradas"... */
  unitPlural: string;
  /** Cómo llamamos al que paga: "comprador", "socio", "participante"... */
  payerSingular: string;
  payerPlural: string;
  /** Cómo describir la acción de unirse */
  ctaJoin: string;
  /** Descripción para landing/form */
  shortDesc: string;
  /** Hint para campo "importe por persona" */
  amountHint: string;
  /** Hint para campo "número esperado" */
  expectedHint: string;
  /** Color tailwind gradient */
  gradient: string;
}


const COPY: Record<GroupBuyType, VerticalCopy> = {
  raffle_share: {
    emoji: '🎟️',
    shortLabel: 'Sorteo de peña',
    unitSingular: 'papeleta',
    unitPlural: 'papeletas',
    payerSingular: 'miembro de la peña',
    payerPlural: 'miembros',
    ctaJoin: 'Comprar mis papeletas',
    shortDesc: 'Tu peña compra varias papeletas y cada miembro queda nominativo a las suyas con DNI.',
    amountHint: 'Lo que cuesta una papeleta',
    expectedHint: 'Cuántas papeletas tendrá vuestra peña',
    gradient: 'from-blue-600 to-cyan-600',
  },
  papeleta_sitio: {
    emoji: '⛪',
    shortLabel: 'Papeleta de sitio',
    unitSingular: 'papeleta',
    unitPlural: 'papeletas de sitio',
    payerSingular: 'hermano',
    payerPlural: 'hermanos',
    ctaJoin: 'Sacar mi papeleta de sitio',
    shortDesc: 'Cada hermano paga su papeleta con DNI nominativo. Sin Excel ni reclamaciones.',
    amountHint: 'Lo que cuesta la papeleta',
    expectedHint: 'Cuántos hermanos van a sacar papeleta',
    gradient: 'from-purple-600 to-indigo-700',
  },
  ticket_share: {
    emoji: '🎫',
    shortLabel: 'Entradas en grupo',
    unitSingular: 'entrada',
    unitPlural: 'entradas',
    payerSingular: 'persona',
    payerPlural: 'personas',
    ctaJoin: 'Comprar mi entrada',
    shortDesc: 'Cada uno paga su entrada con QR. Tú no adelantas nada.',
    amountHint: 'Lo que cuesta una entrada',
    expectedHint: 'Cuántas personas vais a ir',
    gradient: 'from-orange-500 to-red-500',
  },
  membership_quota: {
    emoji: '💳',
    shortLabel: 'Cuota de asociación',
    unitSingular: 'cuota',
    unitPlural: 'cuotas',
    payerSingular: 'socio',
    payerPlural: 'socios',
    ctaJoin: 'Pagar mi cuota',
    shortDesc: 'Cobra a tus socios sin domiciliación obligatoria. Pago con Bizum o tarjeta.',
    amountHint: 'Cuánto paga cada socio',
    expectedHint: 'Cuántos socios calculas',
    gradient: 'from-emerald-600 to-teal-600',
  },
  group_gift: {
    emoji: '🎁',
    shortLabel: 'Regalo conjunto',
    unitSingular: 'aportación',
    unitPlural: 'aportaciones',
    payerSingular: 'persona',
    payerPlural: 'personas',
    ctaJoin: 'Poner mi parte',
    shortDesc: 'Cumple, despedida, jubilación... cada uno pone su parte sin adelantos.',
    amountHint: 'Cuánto pone cada persona',
    expectedHint: 'Entre cuántas personas',
    gradient: 'from-pink-500 to-rose-500',
  },
  event_crowdfund: {
    emoji: '🚀',
    shortLabel: 'Crowdfunding evento',
    unitSingular: 'reserva',
    unitPlural: 'reservas',
    payerSingular: 'asistente',
    payerPlural: 'asistentes',
    ctaJoin: 'Reservar mi entrada',
    shortDesc: 'Si llegamos al mínimo, sucede. Si no, devolvemos todo. Cero riesgo.',
    amountHint: 'Lo que cuesta una reserva',
    expectedHint: 'Mínimo de reservas para que salga',
    gradient: 'from-indigo-500 to-purple-600',
  },
  porra: {
    emoji: '⚽',
    shortLabel: 'Porra deportiva',
    unitSingular: 'participación',
    unitPlural: 'participaciones',
    payerSingular: 'participante',
    payerPlural: 'participantes',
    ctaJoin: 'Apuntarme a la porra',
    shortDesc: 'Cada uno paga su parte, predice el resultado, gana el que más acierta.',
    amountHint: 'Cuánto pone cada uno en la porra',
    expectedHint: 'Cuántos vais a participar',
    gradient: 'from-green-600 to-emerald-700',
  },
};


export function copyFor(type: string): VerticalCopy {
  return (COPY as Record<string, VerticalCopy>)[type] || COPY.group_gift;
}


/**
 * Construye frase "Ya somos X de Y pagando" en lenguaje correcto al vertical.
 */
export function progressPhrase(type: string, paid: number, expected?: number): string {
  const c = copyFor(type);
  if (!expected) {
    return `${paid} ${paid === 1 ? c.payerSingular + ' ha pagado' : c.payerPlural + ' han pagado'}`;
  }
  const remaining = expected - paid;
  if (remaining <= 0) {
    return `¡Completo! ${paid} ${c.payerPlural} pagaron`;
  }
  if (remaining === 1) {
    return `${paid} de ${expected} ${c.payerPlural} · falta 1`;
  }
  return `${paid} de ${expected} ${c.payerPlural} · faltan ${remaining}`;
}


/**
 * Etiqueta legible de estado del cobro.
 */
export function statusLabel(status: string): string {
  const map: Record<string, string> = {
    draft: 'Borrador',
    open: 'Abierto',
    completed: 'Completo',
    expired: 'Caducado',
    cancelled: 'Cancelado',
    settled: 'Dinero entregado',
  };
  return map[status] || status;
}


/**
 * Etiqueta legible para la política on_incomplete.
 */
export function incompletePolicyLabel(policy: string): string {
  const map: Record<string, string> = {
    organizer_covers: 'Yo cubro lo que falte',
    refund_all: 'Devolver el dinero a todos',
    cancel_remaining: 'Quedarse con lo recaudado',
    proceed_anyway: 'Seguir adelante igual',
  };
  return map[policy] || policy;
}


/**
 * Texto del badge de estado (compatible con tabla, con descripción larga al hover).
 */
export function statusDescription(status: string): string {
  const map: Record<string, string> = {
    draft: 'Aún no lo has compartido con nadie',
    open: 'Aceptando pagos',
    completed: 'Ya pagaron todos los que esperabas',
    expired: 'El plazo terminó sin completarse',
    cancelled: 'Tú lo cancelaste',
    settled: 'El dinero ya está en tu cuenta',
  };
  return map[status] || '';
}
